import { useState, useRef, useEffect } from "react";
import { Sparkles, Send, Plus, MessageSquare, Trash2, Bot } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Quanti contratti attivi ho?",
  "Quali clienti sono ad alto rischio?",
  "Mostrami il fatturato degli ultimi 30 giorni",
  "Quante multe pendenti devo ancora rinotificare?",
  "Quale veicolo è il più redditizio?",
  "Quanti eventi compliance ho avuto questa settimana?",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    try {
      const r = await api.get("/ai-assistant/sessions");
      setSessions(r.data?.sessions || []);
    } catch (e) { /* silent */ }
  };

  const loadHistory = async (sid) => {
    try {
      const r = await api.get(`/ai-assistant/history/${sid}`);
      setMessages(r.data?.items || []);
      setSessionId(sid);
    } catch (e) { toast.error("Errore caricamento storico"); }
  };

  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg, created_at: new Date().toISOString() }]);
    setSending(true);
    try {
      const r = await api.post("/ai-assistant/chat", { message: msg, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages((prev) => [...prev, { role: "assistant", content: r.data.answer, context_keys: r.data.context_keys, created_at: new Date().toISOString() }]);
      loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
    } finally { setSending(false); }
  };

  const newChat = () => { setMessages([]); setSessionId(null); };

  const deleteSession = async (sid) => {
    if (!window.confirm("Eliminare la conversazione?")) return;
    try {
      await api.delete(`/ai-assistant/sessions/${sid}`);
      if (sid === sessionId) newChat();
      loadSessions();
      toast.success("Conversazione eliminata");
    } catch (e) { toast.error("Errore"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 h-[calc(100vh-7rem)]" data-testid="ai-assistant-page">
      {/* Sidebar sessioni — collapsed on mobile, shown as horizontal carousel */}
      <aside className="bg-white/[0.02] border border-white/10 rounded-2xl p-3 flex flex-col max-h-[180px] lg:max-h-none overflow-hidden">
        <button data-testid="ai-new-chat" onClick={newChat} className="w-full px-3 py-2 rounded-lg bg-[#ff0033] text-white text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-[#cc0029] transition">
          <Plus size={13} /> Nuova Chat
        </button>
        <div className="mt-3 text-[10px] uppercase tracking-widest text-white/40 px-1">Cronologia</div>
        <div className="flex-1 overflow-y-auto mt-2 space-y-1">
          {sessions.length === 0 && <div className="text-xs text-white/30 px-2 py-3">Nessuna conversazione</div>}
          {sessions.map((s) => (
            <div key={s.session_id} className={`group rounded-lg px-2 py-2 cursor-pointer flex items-start gap-2 ${s.session_id === sessionId ? "bg-[#ff0033]/15 border border-[#ff0033]/40" : "bg-white/[0.02] border border-transparent hover:border-white/10"}`}>
              <MessageSquare size={12} className="text-white/40 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0" onClick={() => loadHistory(s.session_id)}>
                <div className="text-xs truncate">{s.last_msg}</div>
                <div className="text-[10px] text-white/30 mt-0.5">{s.count} msg · {s.last_at?.slice(11,16)}</div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }} className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-rose-400"><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      </aside>

      {/* Chat area */}
      <main className="flex flex-col bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
        <header className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#ff0033] to-[#ff3366] flex items-center justify-center">
              <Bot size={20} className="text-white" />
            </div>
            <div>
              <div className="font-display text-lg font-black">Business Advisor AI</div>
              <div className="text-[10px] text-white/40 uppercase tracking-widest">GPT-4o · accesso ai dati live</div>
            </div>
          </div>
          <Sparkles className="text-[#ff0033]" size={18} />
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
          {messages.length === 0 && (
            <div className="space-y-6 max-w-2xl mx-auto pt-4">
              <div className="text-center">
                <Bot className="mx-auto text-[#ff0033]" size={48} />
                <h2 className="font-display text-2xl font-black mt-3">Ciao, come posso aiutarti?</h2>
                <p className="text-sm text-white/40 mt-2">Sono il tuo consulente AI. Conosco tutti i dati del gestionale.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} data-testid={`ai-suggestion-${i}`} onClick={() => send(s)} className="text-left text-xs bg-white/[0.02] border border-white/10 rounded-xl p-3 hover:border-[#ff0033]/40 hover:bg-[#ff0033]/5 transition">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} data-testid={`ai-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "bg-[#ff0033] text-white" : "bg-white/[0.04] border border-white/10"}`}>
                {m.role === "assistant" && (
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#ff0033] mb-1">
                    <Bot size={10} /> Advisor
                  </div>
                )}
                <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
                {m.context_keys && m.context_keys.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-white/5">
                    {m.context_keys.map((k) => (
                      <span key={k} className="text-[9px] uppercase tracking-widest bg-white/5 border border-white/10 rounded-full px-2 py-0.5 text-white/40">{k}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3 text-sm">
                <div className="flex items-center gap-2 text-white/60"><Bot size={12} /> sto pensando...</div>
              </div>
            </div>
          )}
        </div>

        <footer className="border-t border-white/5 p-3">
          <div className="flex gap-2">
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
              placeholder="Chiedi qualsiasi cosa sul tuo business..."
              disabled={sending}
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#ff0033]/50"
            />
            <button data-testid="ai-send" onClick={() => send()} disabled={sending || !input.trim()} className="px-5 py-3 rounded-xl bg-[#ff0033] text-white disabled:opacity-40 hover:bg-[#cc0029] transition">
              <Send size={16} />
            </button>
          </div>
        </footer>
      </main>
    </div>
  );
}
